import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Select,
  FormLabel,
  FormControl,
  useColorModeValue,
  useDisclosure,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Input,
  NumberInput,
  NumberInputField,
  Icon,
  Divider,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useBreakpointValue,
  Flex
} from '@chakra-ui/react';
import { AddIcon, UpDownIcon, SettingsIcon } from '@chakra-ui/icons';

// モバイル用ボトムシートフィルターコンポーネント
function MobileFilterBottomSheet({ isOpen, onClose, filters, onAddFilter, onRemoveFilter, isTsubo, config, items }) {
  const [selectedProperty, setSelectedProperty] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');

  // 利用可能なフィルタープロパティを取得
  const getAvailableProperties = () => {
    const properties = [];
    
    // 特別なフィルター
    properties.push(
      { key: 'showOnlyLiked', label: 'お気に入り', type: 'special' },
      { key: 'showOnlyUserEquipment', label: `お客様にて登録した${config.key === 'floorplan' ? '間取り' : '設備'}`, type: 'special' }
    );

    // 設定フィールドから追加
    if (config.fields) {
      config.fields.forEach(field => {
        if (field.name && field.label) {
          const excludedFields = ['images', 'default_quantity'];
          if (!excludedFields.includes(field.name)) {
            properties.push({
              key: field.name,
              label: field.label,
              type: field.type,
              options: field.options,
              unit: field.unit
            });
          }
        }
      });
    }

    return properties.filter(p => p.key && p.label);
  };

  // 文字列フィールドの選択肢を取得
  const getStringOptions = (fieldName) => {
    return Array.from(
      new Set(
        items
          .map(item => item[fieldName])
          .filter(v => v && v !== '')
          .map(v => v.trim())
      )
    );
  };

  const selectedPropertyData = getAvailableProperties().find(p => p.key === selectedProperty);

  const handleAddFilter = () => {
    if (!selectedProperty) return;

    const property = selectedPropertyData;
    let filter = {
      key: selectedProperty,
      label: property.label,
      type: property.type
    };

    switch (property.type) {
      case 'special':
        filter.value = true;
        break;
      case 'string':
        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return;
        filter.value = Array.isArray(filterValue) ? filterValue : [filterValue];
        break;
      case 'select':
        if (!filterValue) return;
        filter.value = filterValue;
        break;
      case 'number':
        const min = minValue ? parseFloat(minValue) : undefined;
        const max = maxValue ? parseFloat(maxValue) : undefined;
        if (min === undefined && max === undefined) return;
        filter.min = min;
        filter.max = max;
        filter.unit = property.unit;
        break;
      case 'boolean':
        filter.value = true;
        break;
      default:
        return;
    }

    onAddFilter(filter);
    
    // リセット
    setSelectedProperty('');
    setFilterValue('');
    setMinValue('');
    setMaxValue('');
  };

  const renderValueInput = () => {
    if (!selectedPropertyData) return null;

    switch (selectedPropertyData.type) {
      case 'special':
        return null;

      case 'string':
        const options = getStringOptions(selectedProperty);
        const selectedValues = Array.isArray(filterValue) ? filterValue : filterValue ? [filterValue] : [];
        
        return (
          <FormControl>
            <FormLabel fontSize="md" fontWeight="medium">{selectedPropertyData.label}（複数選択可）</FormLabel>
            <VStack spacing={3} align="stretch" maxH="40vh" overflowY="auto">
              {options.map(option => (
                <Checkbox
                  key={option}
                  isChecked={selectedValues.includes(option)}
                  onChange={(e) => {
                    let newValues;
                    if (e.target.checked) {
                      newValues = [...selectedValues, option];
                    } else {
                      newValues = selectedValues.filter(v => v !== option);
                    }
                    setFilterValue(newValues);
                  }}
                  size="md"
                >
                  <Text fontSize="md">{option}</Text>
                </Checkbox>
              ))}
            </VStack>
          </FormControl>
        );

      case 'select':
        return (
          <FormControl>
            <FormLabel fontSize="sm">{selectedPropertyData.label}</FormLabel>
            <Select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder="選択してください"
              size="sm"
            >
              {selectedPropertyData.options?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </Select>
          </FormControl>
        );

      case 'number':
        const unit = (isTsubo && selectedPropertyData.unit === 'm2') ? '坪' : selectedPropertyData.unit;
        return (
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">{selectedPropertyData.label}の範囲{unit ? `（${unit}）` : ''}</FormLabel>
              <HStack spacing={2} align="center">
                <NumberInput size="sm" flex="1">
                  <NumberInputField
                    value={minValue}
                    onChange={(e) => setMinValue(e.target.value)}
                    placeholder="下限"
                  />
                </NumberInput>
                <Text fontSize="sm" color="gray.500" minW="20px" textAlign="center">～</Text>
                <NumberInput size="sm" flex="1">
                  <NumberInputField
                    value={maxValue}
                    onChange={(e) => setMaxValue(e.target.value)}
                    placeholder="上限"
                  />
                </NumberInput>
              </HStack>
            </FormControl>
          </VStack>
        );

      case 'boolean':
        return (
          <Text fontSize="sm" color="gray.600">
            この条件は値の設定は不要です
          </Text>
        );

      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent borderTopRadius="xl" borderBottomRadius="0" maxH="90vh" mt="auto">
        <ModalHeader borderBottomWidth="1px" pb={4}>
          条件で絞り込み
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* 現在の条件一覧 */}
            {filters.length > 0 && (
              <Box>
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    設定中の条件
                  </Text>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    fontSize="xs"
                    onClick={() => {
                      // 全ての条件をクリア
                      filters.forEach((_, index) => {
                        onRemoveFilter(index);
                      });
                    }}
                    _hover={{ bg: "gray.200" }}
                  >
                    すべてクリア
                  </Button>
                </Flex>
                <Wrap spacing={2}>
                  {filters.map((filter, index) => (
                    <WrapItem key={index}>
                      <ActiveFilterTag 
                        filter={filter} 
                        onRemove={() => onRemoveFilter(index)}
                        isTsubo={isTsubo}
                      />
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}

            {/* 新しい条件を追加 */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3} color="gray.700">
                新しい条件を追加
              </Text>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">項目</FormLabel>
                  <Select
                    value={selectedProperty}
                    onChange={(e) => setSelectedProperty(e.target.value)}
                    placeholder="条件を選択"
                    size="sm"
                  >
                    {getAvailableProperties().map(property => (
                      <option key={property.key} value={property.key}>
                        {property.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {selectedProperty && (
                  <>
                    <Divider />
                    {renderValueInput()}
                  </>
                )}

                <Button
                  onClick={handleAddFilter}
                  colorScheme="yellow"
                  size="md"
                  isDisabled={!selectedProperty || (
                    selectedPropertyData?.type === 'string' && !filterValue
                  )}
                  w="100%"
                >
                  条件を追加
                </Button>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

// アクティブなフィルターを表示するタグコンポーネント
function ActiveFilterTag({ filter, onRemove, isTsubo = false }) {
  const tagBg = useColorModeValue('yellow.100', 'yellow.800');
  const tagColor = useColorModeValue('yellow.800', 'yellow.100');

  const getDisplayText = () => {
    switch (filter.type) {
      case 'boolean':
        return filter.label;
      case 'string':
        if (Array.isArray(filter.value)) {
          return `${filter.label}: ${filter.value.join(', ')}`;
        }
        return `${filter.label}: ${filter.value}`;
      case 'select':
        return `${filter.label}: ${filter.value}`;
      case 'number':
        // 坪表記の場合は単位を変更
        const unit = (isTsubo && filter.unit === 'm2') ? '坪' : filter.unit;
        if (filter.min !== undefined && filter.max !== undefined) {
          return `${filter.label}: ${filter.min}～${filter.max}${unit || ''}`;
        } else if (filter.min !== undefined) {
          return `${filter.label}: ${filter.min}${unit || ''}以上`;
        } else if (filter.max !== undefined) {
          return `${filter.label}: ${filter.max}${unit || ''}以下`;
        }
        return filter.label;
      case 'special':
        return filter.label;
      default:
        return filter.label;
    }
  };

  return (
    <Tag
      size="md"
      variant="solid"
      bg="linear-gradient(135deg, yellow.400, yellow.500)"
      color="gray.800"
      borderRadius="full"
      fontWeight="semibold"
      px={3}
      py={1}
      border="2px solid"
      borderColor="yellow.300"
      _hover={{ 
        transform: 'scale(1.05)', 
        boxShadow: 'lg',
        bg: "linear-gradient(135deg, yellow.500, yellow.600)"
      }}
      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
      boxShadow="md"
    >
      <TagLabel>{getDisplayText()}</TagLabel>
      <TagCloseButton 
        onClick={onRemove}
        color="gray.600"
        _hover={{ 
          color: "gray.800",
          bg: "whiteAlpha.300"
        }}
        borderRadius="full"
      />
    </Tag>
  );
}

// フィルター追加のポップオーバーコンポーネント
function AddFilterPopover({ config, onAddFilter, items = [], isTsubo = false }) {
  const [selectedProperty, setSelectedProperty] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 利用可能なフィルタープロパティを取得
  const getAvailableProperties = () => {
    const properties = [];
    
    // 特別なフィルター
    properties.push(
      { key: 'showOnlyLiked', label: 'お気に入り', type: 'special' },
      { key: 'showOnlyUserEquipment', label: `お客様にて登録した${config.key === 'floorplan' ? '間取り' : '設備'}`, type: 'special' }
    );

    // 設定フィールドから追加（manufacturerも含む）
    if (config.fields) {
      config.fields.forEach(field => {
        // key(name)とlabelが空でないものだけ追加
        if (field.name && field.label) {
          // 画像フィールドとデフォルト数量フィールドを除外
          const excludedFields = ['images', 'default_quantity'];
          if (!excludedFields.includes(field.name)) {
            properties.push({
              key: field.name,
              label: field.label,
              type: field.type,
              options: field.options,
              unit: field.unit
            });
          }
        }
      });
    }

    // keyまたはlabelが空のものを除外
    return properties.filter(p => p.key && p.label);
  };

  // 文字列フィールドの選択肢を取得
  const getStringOptions = (fieldName) => {
    return Array.from(
      new Set(
        items
          .map(item => item[fieldName])
          .filter(v => v && v !== '')
          .map(v => v.trim()) // 末尾のスペースを除去
      )
    );
  };

  const selectedPropertyData = getAvailableProperties().find(p => p.key === selectedProperty);

  const handleAddFilterToPopover = () => {
    if (!selectedProperty) return;

    const property = selectedPropertyData;
    let filter = {
      key: selectedProperty,
      label: property.label,
      type: property.type
    };

    switch (property.type) {
      case 'special':
        filter.value = true;
        break;
      case 'string':
        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return;
        filter.value = Array.isArray(filterValue) ? filterValue : [filterValue];
        break;
      case 'select':
        if (!filterValue) return;
        filter.value = filterValue;
        break;
      case 'number':
        const min = minValue ? parseFloat(minValue) : undefined;
        const max = maxValue ? parseFloat(maxValue) : undefined;
        if (min === undefined && max === undefined) return;
        filter.min = min;
        filter.max = max;
        filter.unit = property.unit;
        break;
      case 'boolean':
        filter.value = true;
        break;
      default:
        return;
    }

    onAddFilter(filter);
    
    // リセット
    setSelectedProperty('');
    setFilterValue('');
    setMinValue('');
    setMaxValue('');
    
    // ポップオーバーを閉じる
    onClose();
  };

  const renderValueInput = () => {
    if (!selectedPropertyData) return null;

    switch (selectedPropertyData.type) {
      case 'special':
        return null;

      case 'string':
        const options = getStringOptions(selectedProperty);
        const selectedValues = Array.isArray(filterValue) ? filterValue : filterValue ? [filterValue] : [];
        
        return (
          <FormControl>
            <FormLabel fontSize="md" fontWeight="medium">{selectedPropertyData.label}（複数選択可）</FormLabel>
            <VStack 
              spacing={3} 
              align="stretch" 
              maxH="40vh" 
              overflowY="auto"
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              {options.map(option => (
                <Checkbox
                  key={option}
                  isChecked={selectedValues.includes(option)}
                  onChange={(e) => {
                    let newValues;
                    if (e.target.checked) {
                      newValues = [...selectedValues, option];
                    } else {
                      newValues = selectedValues.filter(v => v !== option);
                    }
                    setFilterValue(newValues);
                  }}
                  size="md"
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                >
                  <Text fontSize="md">{option}</Text>
                </Checkbox>
              ))}
            </VStack>
          </FormControl>
        );

      case 'select':
        return (
          <FormControl>
            <FormLabel fontSize="sm">{selectedPropertyData.label}</FormLabel>
            <Select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder="選択してください"
              size="sm"
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              {selectedPropertyData.options?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </Select>
          </FormControl>
        );

      case 'number':
        // 坪表記の場合は単位を変更
        const unit = (isTsubo && selectedPropertyData.unit === 'm2') ? '坪' : selectedPropertyData.unit;
        return (
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">{selectedPropertyData.label}の範囲{unit ? `（${unit}）` : ''}</FormLabel>
              <HStack spacing={2} align="center">
                <NumberInput size="sm" flex="1">
                  <NumberInputField
                    value={minValue}
                    onChange={(e) => setMinValue(e.target.value)}
                    placeholder="下限"
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  />
                </NumberInput>
                <Text fontSize="sm" color="gray.500" minW="20px" textAlign="center">～</Text>
                <NumberInput size="sm" flex="1">
                  <NumberInputField
                    value={maxValue}
                    onChange={(e) => setMaxValue(e.target.value)}
                    placeholder="上限"
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  />
                </NumberInput>
              </HStack>
            </FormControl>
          </VStack>
        );

      case 'boolean':
        return (
          <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')}>
            このフィルターは値の設定は不要です
          </Text>
        );

      default:
        return null;
    }
  };

  return (
    <Popover 
      placement="bottom-start"
      closeOnBlur={false}
      closeOnEsc={true}
      autoFocus={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <PopoverTrigger>
        <Button
          leftIcon={<AddIcon />}
          variant="solid"
          size="sm"
          colorScheme="yellow"
          borderRadius="full"
          bg="yellow.400"
          color="gray.800"
          fontWeight="bold"
          px={4}
          py={2}
          _hover={{ 
            bg: 'yellow.500', 
            transform: 'translateY(-2px)',
            boxShadow: 'lg'
          }}
          _active={{
            bg: 'yellow.600',
            transform: 'translateY(0px)',
          }}
          transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
          boxShadow="md"
        >
          🎯 条件を追加
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        bg={bgColor} 
        borderColor={borderColor} 
        boxShadow="lg" 
        w="300px"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="semibold">条件を追加</PopoverHeader>
        <PopoverBody
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">項目</FormLabel>
              <Select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                placeholder="条件を選択"
                size="sm"
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
              >
                {getAvailableProperties().map(property => (
                  <option key={property.key} value={property.key}>
                    {property.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {selectedProperty && (
              <>
                <Divider />
                {renderValueInput()}
              </>
            )}

            <Button
              onClick={handleAddFilterToPopover}
              colorScheme="yellow"
              size="sm"
              isDisabled={!selectedProperty || (
                selectedPropertyData?.type === 'search' && !filterValue.trim()
              )}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              追加
            </Button>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

export default function EquipmentFilter({
  config,
  filterState,
  onFilterChange,
  items = [],
  isTsubo = false,
  sortState = {},
  onSortChange = () => {},
}) {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.800');

  // アクティブなフィルターを管理
  const [activeFilters, setActiveFilters] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // モバイル用フィルターの状態
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // 初期化用のrefでpropsの初期値を保存
  const initialPropsRef = useRef({
    filterState,
    config
  });
  
  // 前回の値を保存するref
  const prevStateRef = useRef({
    filterState: null
  });

  // configが変更された時にactiveFiltersをリセット
  React.useEffect(() => {
    if (config && config !== initialPropsRef.current.config) {
      setActiveFilters([]);
      setIsInitialized(false);
      initialPropsRef.current.config = config;
    }
  }, [config]);

  // filterStateが外部から強制的にクリアされた時のみactiveFiltersをリセット
  React.useEffect(() => {
    // 初期化完了前は何もしない
    if (!isInitialized) return;
    
    // filterStateが初期状態（全てfalse/空）にリセットされた場合
    const isFilterStateCleared = filterState && 
      !filterState.showOnlyLiked && 
      !filterState.showRecommendedOnly &&
      !filterState.showOnlyUserEquipment &&
      (!config?.fields || config.fields.every(field => {
        if (field.type === 'number') {
          return (!filterState[`min_${field.name}`] || filterState[`min_${field.name}`] === '') && 
                 (!filterState[`max_${field.name}`] || filterState[`max_${field.name}`] === '');
        }
        if (field.type === 'select' || field.type === 'string') {
          return !filterState[field.name] || filterState[field.name] === '';
        }
        if (field.type === 'boolean') {
          return !filterState[field.name];
        }
        return true;
      }));

    // 外部からのリセットかどうかを判定（自分が送信したものでない場合のみ）
    const isExternalReset = isFilterStateCleared && 
      activeFilters.length > 0 && 
      JSON.stringify(filterState) !== JSON.stringify(prevStateRef.current.filterState);

    if (isExternalReset) {
      setActiveFilters([]);
    }
  }, [filterState, config, isInitialized, activeFilters.length]);

  // 既存のfilterStateからアクティブフィルターを初期化（一回だけ）
  React.useEffect(() => {
    if (isInitialized) return;
    if (!filterState || !config) return;
    
    const { filterState: initialFilterState, config: initialConfig } = initialPropsRef.current;
    const initialFilters = [];
    
    // 特別なフィルターは明示的にtrueの場合のみ
    if (initialFilterState.showOnlyLiked === true) {
      initialFilters.push({
        key: 'showOnlyLiked',
        label: 'お気に入り',
        type: 'special',
        value: true
      });
    }
    
    if (initialFilterState.showRecommendedOnly === true) {
      initialFilters.push({
        key: 'showRecommendedOnly',
        label: 'おすすめ',
        type: 'special',
        value: true
      });
    }
    
    if (initialFilterState.showOnlyUserEquipment === true) {
      initialFilters.push({
        key: 'showOnlyUserEquipment',
        label: `お客様にて登録した${initialConfig.key === 'floorplan' ? '間取り' : '設備'}`,
        type: 'special',
        value: true
      });
    }

    // config.fieldsからアクティブなフィルターを検出
    if (initialConfig.fields) {
      initialConfig.fields.forEach(field => {
        if (field.type === 'number') {
          const minKey = `min_${field.name}`;
          const maxKey = `max_${field.name}`;
          const minValue = initialFilterState[minKey];
          const maxValue = initialFilterState[maxKey];
          
          // 数値が実際に入力されている場合のみ（空文字列、null、undefinedを除外）
          const hasMinValue = minValue !== undefined && minValue !== null && minValue !== '' && !isNaN(parseFloat(minValue));
          const hasMaxValue = maxValue !== undefined && maxValue !== null && maxValue !== '' && !isNaN(parseFloat(maxValue));
          
          if (hasMinValue || hasMaxValue) {
            initialFilters.push({
              key: field.name,
              label: field.label,
              type: 'number',
              min: hasMinValue ? parseFloat(minValue) : undefined,
              max: hasMaxValue ? parseFloat(maxValue) : undefined,
              unit: field.unit
            });
          }
        } else if (field.type === 'string') {
          const value = initialFilterState[field.name];
          // 空でない文字列の場合のみ
          if (value && value.trim() !== '') {
            initialFilters.push({
              key: field.name,
              label: field.label,
              type: 'string',
              value: value
            });
          }
        } else if (field.type === 'boolean') {
          const value = initialFilterState[field.name];
          // 明示的にtrueの場合のみ
          if (value === true) {
            initialFilters.push({
              key: field.name,
              label: field.label,
              type: 'boolean',
              value: true
            });
          }
        } else if (field.type === 'select') {
          const value = initialFilterState[field.name];
          // 空でない値の場合のみ
          if (value && value.trim() !== '') {
            initialFilters.push({
              key: field.name,
              label: field.label,
              type: 'select',
              value: value
            });
          }
        }
      });
    }

    setActiveFilters(initialFilters);
    setIsInitialized(true);
    
    // 初期状態をprevStateRefに設定
    prevStateRef.current.filterState = initialFilterState;
    
    // 初期化時にactiveFiltersが空で、かつ既存のfilterStateが空でない場合のみフィルターをクリア
    if (initialFilters.length === 0) {
      const hasExistingFilters = initialFilterState && (
        initialFilterState.showOnlyLiked ||
        initialFilterState.showRecommendedOnly ||
        initialFilterState.showOnlyUserEquipment ||
        (initialConfig.fields && initialConfig.fields.some(field => {
          if (field.type === 'number') {
            return initialFilterState[`min_${field.name}`] || initialFilterState[`max_${field.name}`];
          }
          if (field.type === 'select' || field.type === 'string') {
            return initialFilterState[field.name] && initialFilterState[field.name] !== '';
          }
          if (field.type === 'boolean') {
            return initialFilterState[field.name] === true;
          }
          return false;
        }))
      );
      
      // 既存のフィルターがある場合のみクリア処理を実行
      if (hasExistingFilters) {
        const clearedFilterState = {};
        
        // デフォルト値でリセット
        clearedFilterState.showOnlyLiked = false;
        clearedFilterState.showRecommendedOnly = false;
        clearedFilterState.showOnlyUserEquipment = false;
        
        // 既存のフィールドをリセット
        if (initialConfig.fields) {
          initialConfig.fields.forEach(field => {
            if (field.type === 'number') {
              clearedFilterState[`min_${field.name}`] = '';
              clearedFilterState[`max_${field.name}`] = '';
            } else {
              clearedFilterState[field.name] = field.type === 'boolean' ? false : '';
            }
          });
        }
        
        // 初期化後にクリアな状態を送信
        setTimeout(() => {
          onFilterChange(clearedFilterState);
          prevStateRef.current.filterState = clearedFilterState;
        }, 0);
      }
    }
  }, [filterState, config]); // filterStateとconfigが準備されたら実行

  // アクティブフィルターが変更されたときにfilterStateを更新（初期化後のみ）
  React.useEffect(() => {
    if (!isInitialized) return;
    
    const newFilterState = {};
    
    // デフォルト値でリセット
    newFilterState.showOnlyLiked = false;
    newFilterState.showRecommendedOnly = false;
    newFilterState.showOnlyUserEquipment = false;
    
    // 既存のフィールドをリセット
    if (config.fields) {
      config.fields.forEach(field => {
        if (field.type === 'number') {
          newFilterState[`min_${field.name}`] = '';
          newFilterState[`max_${field.name}`] = '';
        } else {
          newFilterState[field.name] = field.type === 'boolean' ? false : '';
        }
      });
    }
    
    // アクティブフィルターから再構築（空の場合はデフォルト値のみ）
    activeFilters.forEach(filter => {
      switch (filter.key) {
        case 'showOnlyLiked':
          newFilterState.showOnlyLiked = true;
          break;
        case 'showRecommendedOnly':
          newFilterState.showRecommendedOnly = true;
          break;
        case 'showOnlyUserEquipment':
          newFilterState.showOnlyUserEquipment = true;
          break;
        default:
          if (filter.type === 'number') {
            if (filter.min !== undefined) {
              newFilterState[`min_${filter.key}`] = filter.min;
            }
            if (filter.max !== undefined) {
              newFilterState[`max_${filter.key}`] = filter.max;
            }
          } else {
            newFilterState[filter.key] = filter.value;
          }
      }
    });

    // filterStateの更新（値が変更された場合のみ）
    const prevFilterState = prevStateRef.current.filterState;
    const newStateString = JSON.stringify(newFilterState);
    const prevStateString = JSON.stringify(prevFilterState);
    
    if (newStateString !== prevStateString) {
      onFilterChange(newFilterState);
      prevStateRef.current.filterState = newFilterState;
    }
  }, [activeFilters, isInitialized, config]);



  const handleAddFilter = (filter) => {
    // 同じキーのフィルターが既に存在する場合は置き換え
    const existingIndex = activeFilters.findIndex(f => f.key === filter.key);
    if (existingIndex >= 0) {
      const newFilters = [...activeFilters];
      newFilters[existingIndex] = filter;
      setActiveFilters(newFilters);
    } else {
      setActiveFilters([...activeFilters, filter]);
    }
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = activeFilters.filter(f => f.key !== filterKey);
    setActiveFilters(newFilters);
    
    // フィルターが残っている場合も含めて、常に状態を再構築
    const newFilterState = {};
    
    // デフォルト値でリセット
    newFilterState.showOnlyLiked = false;
    newFilterState.showRecommendedOnly = false;
    newFilterState.showOnlyUserEquipment = false;
    
    // 既存のフィールドをリセット
    if (config.fields) {
      config.fields.forEach(field => {
        if (field.type === 'number') {
          newFilterState[`min_${field.name}`] = '';
          newFilterState[`max_${field.name}`] = '';
        } else {
          newFilterState[field.name] = field.type === 'boolean' ? false : '';
        }
      });
    }
    
    // 残ったフィルターから状態を再構築
    newFilters.forEach(filter => {
      switch (filter.key) {
        case 'showOnlyLiked':
          newFilterState.showOnlyLiked = true;
          break;
        case 'showRecommendedOnly':
          newFilterState.showRecommendedOnly = true;
          break;
        case 'showOnlyUserEquipment':
          newFilterState.showOnlyUserEquipment = true;
          break;
        default:
          if (filter.type === 'number') {
            if (filter.min !== undefined) {
              newFilterState[`min_${filter.key}`] = filter.min;
            }
            if (filter.max !== undefined) {
              newFilterState[`max_${filter.key}`] = filter.max;
            }
          } else {
            newFilterState[filter.key] = filter.value;
          }
      }
    });
    
    // 状態を即座に更新
    onFilterChange(newFilterState);
    
    // prevStateRefも更新
    prevStateRef.current.filterState = newFilterState;
  };

  // ソート機能用の数値フィールド（default_quantityを除外）
  const numberFields = (config.fields || []).filter(field => 
    field.type === 'number' && field.name !== 'default_quantity'
  );

  return (
    <Box 
      borderWidth="1px" 
      borderColor={borderColor} 
      borderRadius="xl" 
      p={6} 
      bg={bgColor}
      shadow="md"
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        bg: 'linear-gradient(90deg, yellow.400, yellow.600, yellow.400)',
        borderTopRadius: 'xl'
      }}
    >
             {/* モバイル用フィルターボタン */}
       {isMobile && (
         <Box mb={4}>
           <Button
             variant="outline"
             size="md"
             colorScheme="gray"
             borderColor="gray.300"
             color="gray.600"
             leftIcon={<SettingsIcon />}
             w="100%"
             onClick={() => setIsMobileFilterOpen(true)}
             _hover={{ bg: "gray.50", borderColor: "gray.400" }}
           >
             条件で絞り込み
           </Button>
           
           {/* アクティブな条件の表示 */}
           {activeFilters.length > 0 && (
             <Box mt={3}>
               <Flex justify="space-between" align="center" mb={2}>
                 <Text fontSize="sm" color="gray.600">
                   設定中の条件: {activeFilters.length}件
                 </Text>
                 <Button
                   size="xs"
                   variant="ghost"
                   colorScheme="gray"
                   onClick={() => setActiveFilters([])}
                   _hover={{ bg: "gray.200" }}
                 >
                   すべてクリア
                 </Button>
               </Flex>
               <Wrap spacing={2}>
                 {activeFilters.slice(0, 3).map((filter, index) => (
                   <WrapItem key={`mobile-${filter.key}-${index}`}>
                     <ActiveFilterTag
                       filter={filter}
                       onRemove={() => handleRemoveFilter(filter.key)}
                       isTsubo={isTsubo}
                     />
                   </WrapItem>
                 ))}
                 {activeFilters.length > 3 && (
                   <WrapItem>
                     <Tag size="sm" colorScheme="gray" variant="outline">
                       <TagLabel>他{activeFilters.length - 3}件</TagLabel>
                     </Tag>
                   </WrapItem>
                 )}
               </Wrap>
             </Box>
           )}
           
           {/* モバイル用ソートUI */}
           <Box mt={4}>
             <HStack spacing={3} align="end">
               <FormControl flex="1">
                 <FormLabel fontSize="sm" mb={1} color="gray.700">
                   並び替え
                 </FormLabel>
                 <Select
                   value={sortState.key || ''}
                   onChange={(e) => onSortChange({ ...sortState, key: e.target.value })}
                   placeholder="選択なし"
                   size="sm"
                   bg="white"
                   borderColor="gray.300"
                   _hover={{ borderColor: 'yellow.400' }}
                   _focus={{ borderColor: 'yellow.500', boxShadow: '0 0 0 1px yellow.500' }}
                 >
                   {config?.enable_popularity !== false && (
                     <option value="popularity">人気順</option>
                   )}
                   {numberFields.map(field => {
                     // 坪表記の場合は単位を変更
                     const unit = (isTsubo && field.unit === 'm2') ? '坪' : field.unit;
                     return (
                       <option key={field.name} value={field.name}>
                         {field.label}{unit ? ` (${unit})` : ''}
                       </option>
                     );
                   })}
                 </Select>
               </FormControl>
               
               <FormControl maxW="120px">
                 <FormLabel fontSize="sm" mb={1} color="gray.700">
                   順序
                 </FormLabel>
                 <Select
                   value={sortState.direction || ''}
                   onChange={(e) => onSortChange({ ...sortState, direction: e.target.value })}
                   placeholder="選択なし"
                   size="sm"
                   bg="white"
                   borderColor="gray.300"
                   _hover={{ borderColor: 'yellow.400' }}
                   _focus={{ borderColor: 'yellow.500', boxShadow: '0 0 0 1px yellow.500' }}
                   isDisabled={sortState.key === 'popularity'}
                 >
                   <option value="asc">昇順 (小→大)</option>
                   <option value="desc">降順 (大→小)</option>
                 </Select>
               </FormControl>
             </HStack>
           </Box>
         </Box>
       )}

      <VStack spacing={6} align="stretch">
        {/* フィルター管理セクション */}
        {!isMobile && (
          <Box>
            <VStack spacing={4} align="stretch">
              {/* フィルターコントロール */}
              <Box 
                p={4} 
                bg={useColorModeValue('gray.50', 'gray.700')} 
                borderRadius="lg"
                border="1px solid"
                borderColor={useColorModeValue('gray.200', 'gray.600')}
              >
              <Wrap spacing={3} align="center">
                {/* フィルター追加ボタン */}
                <WrapItem>
                  <AddFilterPopover
                    config={config}
                    onAddFilter={handleAddFilter}
                    items={items}
                    isTsubo={isTsubo}
                  />
                </WrapItem>
                
                {/* アクティブなフィルタータグ */}
                {activeFilters.map((filter, index) => (
                  <WrapItem key={`${filter.key}-${index}`}>
                    <ActiveFilterTag
                      filter={filter}
                      onRemove={() => handleRemoveFilter(filter.key)}
                      isTsubo={isTsubo}
                    />
                  </WrapItem>
                ))}
                
                {/* フィルタークリアボタン（フィルターがある場合のみ表示） */}
                {activeFilters.length > 0 && (
                  <WrapItem>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      fontSize="xs"
                      onClick={() => setActiveFilters([])}
                      _hover={{ bg: useColorModeValue('gray.200', 'gray.600') }}
                    >
                      すべてクリア
                    </Button>
                  </WrapItem>
                )}
              </Wrap>
              
              {/* フィルターが設定されていない場合のプレースホルダー */}
              {activeFilters.length === 0 && (
                <Text 
                  fontSize="sm" 
                  color={useColorModeValue('gray.500', 'gray.400')} 
                  fontStyle="italic"
                  textAlign="center"
                  py={2}
                >
                  条件が設定されていません
                </Text>
              )}
            </Box>
          </VStack>
        </Box>
        )}

                 {/* 並び替えセクション - デスクトップのみ表示 */}
         {!isMobile && (
           <Box>
             <VStack spacing={4} align="stretch">
               
               {/* 並び替えコントロール */}
               <Box 
                 p={4} 
                 bg={useColorModeValue('gray.50', 'gray.700')} 
                 borderRadius="lg"
                 border="1px solid"
                 borderColor={useColorModeValue('gray.200', 'gray.600')}
               >
                 <HStack spacing={4} align="end">
                   <FormControl maxW="200px">
                     <FormLabel 
                       fontSize="sm" 
                       mb={2}
                       color={useColorModeValue('gray.700', 'gray.300')}
                       fontWeight="medium"
                     >
                       並び替え項目
                     </FormLabel>
                     <Select
                       value={sortState.key || ''}
                       onChange={(e) => onSortChange({ ...sortState, key: e.target.value })}
                       placeholder="選択なし"
                       size="sm"
                       bg={useColorModeValue('white', 'gray.800')}
                       borderColor={useColorModeValue('gray.300', 'gray.600')}
                       _hover={{ borderColor: 'yellow.400' }}
                       _focus={{ borderColor: 'yellow.500', boxShadow: '0 0 0 1px yellow.500' }}
                     >
                       {config?.enable_popularity !== false && (
                         <option value="popularity">人気順</option>
                       )}
                       {numberFields.map(field => {
                         // 坪表記の場合は単位を変更
                         const unit = (isTsubo && field.unit === 'm2') ? '坪' : field.unit;
                         return (
                           <option key={field.name} value={field.name}>
                             {field.label}{unit ? ` (${unit})` : ''}
                           </option>
                         );
                       })}
                     </Select>
                   </FormControl>
                   
                   <FormControl maxW="150px">
                     <FormLabel 
                       fontSize="sm" 
                       mb={2}
                       color={useColorModeValue('gray.700', 'gray.300')}
                       fontWeight="medium"
                     >
                       並び順
                     </FormLabel>
                     <Select
                       value={sortState.direction || ''}
                       onChange={(e) => onSortChange({ ...sortState, direction: e.target.value })}
                       placeholder="選択なし"
                       size="sm"
                       bg={useColorModeValue('white', 'gray.800')}
                       borderColor={useColorModeValue('gray.300', 'gray.600')}
                       _hover={{ borderColor: 'yellow.400' }}
                       _focus={{ borderColor: 'yellow.500', boxShadow: '0 0 0 1px yellow.500' }}
                       isDisabled={sortState.key === 'popularity'}
                     >
                       <option value="asc">昇順 (小→大)</option>
                       <option value="desc">降順 (大→小)</option>
                     </Select>
                   </FormControl>
                 </HStack>
               </Box>
             </VStack>
           </Box>
         )}
      </VStack>
      
      {/* モバイル用ボトムシートフィルター */}
      <MobileFilterBottomSheet
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        filters={activeFilters}
        onAddFilter={handleAddFilter}
        onRemoveFilter={(index) => {
          const filter = activeFilters[index];
          handleRemoveFilter(filter.key);
        }}
        isTsubo={isTsubo}
        config={config}
        items={items}
      />
    </Box>
  );
} 